/**
 * AiPanel — slide-over панель чата справа.
 *
 *  - Position: fixed, right 16, top 16, bottom 16, width 480 (mobile = full)
 *  - Slide-in 320ms cubic-bezier(0.16,1,0.3,1)
 *  - Header: аватар + AI-помощник + новая беседа/закрыть
 *  - Body: либо welcome+suggestions (если history пустая), либо message list
 *  - Composer: attach + autosize textarea + send-кнопка. Cmd/Ctrl+Enter — send. Esc — close.
 *
 *  Стрим: useAiChat. Auto-scroll к низу при добавлении/росте сообщений.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Button, Icon, IconButton } from '@/shared/ui';
import { useAiStore, type AiAttachment } from '@/state/aiStore';
import { useAiChat } from '@/hooks/api/useAiChat';
import { AiMessage } from './AiMessage';
import { AiTyping } from './AiTyping';
import { PulseAvatar } from './PulseAvatar';
import { AiSuggestions } from './AiSuggestions';
import { AiUploader } from './AiUploader';
import { toolCallsToSteps } from './aiToolLabels';
import { zIndex } from '@/theme/tokens';

const MAX_TEXTAREA_HEIGHT = 150;

function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const on = () => setMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return mobile;
}

export function AiPanel() {
  const isMobile = useIsMobile();

  const isOpen = useAiStore((s) => s.isPanelOpen);
  const closePanel = useAiStore((s) => s.closePanel);

  const { messages, isStreaming, awaitingFirstChunk, sendMessage, reset, abort } = useAiChat();

  // Текущее стриминговое сообщение — нужно для извлечения tool_use событий в шаги
  const streamingMessageId = useAiStore((s) => s.streamingMessageId);
  const streamingMessage = streamingMessageId
    ? messages.find((m) => m.id === streamingMessageId)
    : undefined;
  const thinkingSteps = streamingMessage
    ? toolCallsToSteps(
        streamingMessage.toolCalls,
        isStreaming,
        streamingMessage.content.length > 0,
      )
    : undefined;

  const [input, setInput] = useState('');
  const [attached, setAttached] = useState<File[]>([]);
  const [showUploader, setShowUploader] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<string>('');

  // Closing via Esc
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closePanel();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closePanel]);

  // Autosize textarea
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [input]);

  // Auto-scroll to bottom
  // Скроллим на каждом изменении (новое сообщение/новый chunk).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Если пользователь близко к низу — следуем за стримом.
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    const lastContent = messages[messages.length - 1]?.content ?? '';
    const grew = lastContent.length > lastMessageRef.current.length;
    lastMessageRef.current = lastContent;
    if (isNearBottom || messages.length > 0) {
      requestAnimationFrame(() => {
        if (isNearBottom || grew || messages.length === 1) {
          el.scrollTop = el.scrollHeight;
        }
      });
    }
  }, [messages]);

  // Focus input on open
  useEffect(() => {
    if (isOpen) {
      const t = window.setTimeout(() => textareaRef.current?.focus(), 200);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  // Send
  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text && attached.length === 0) return;
    if (isStreaming) return;

    let payloadOverride: string | undefined;
    const attachments: AiAttachment[] | undefined =
      attached.length > 0
        ? attached.map((f) => ({ name: f.name, size: f.size }))
        : undefined;

    if (attached.length > 0) {
      const blocks: string[] = [text];
      for (const f of attached) {
        try {
          const content = await readFileText(f);
          blocks.push(`\n\n[Файл «${f.name}»]:\n\`\`\`\n${content.slice(0, 8000)}\n\`\`\``);
        } catch {
          blocks.push(`\n\n[Не удалось прочитать ${f.name}]`);
        }
      }
      payloadOverride = blocks.join('');
    }

    setInput('');
    setAttached([]);
    setShowUploader(false);

    await sendMessage(text, {
      ...(attachments ? { attachments } : {}),
      ...(payloadOverride ? { payloadOverride } : {}),
    });
  }, [input, attached, isStreaming, sendMessage]);

  // Стоп: рвём стрим, возвращаем последний запрос пользователя в поле ввода —
  // чтобы скорректировать и отправить заново. Частичная наработка ассистента
  // остаётся в ленте (бейдж «Остановлено») и уезжает в контекст следующего хода.
  const handleStop = useCallback(() => {
    if (!isStreaming) return;
    abort();
    const lastUser = [...useAiStore.getState().messages]
      .reverse()
      .find((m) => m.role === 'user');
    const restore = lastUser?.content ?? '';
    // Не затираем, если пользователь уже что-то печатает в поле.
    setInput((cur) => (cur.trim() ? cur : restore));
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
  }, [isStreaming, abort]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      if (isStreaming) handleStop();
      else void handleSend();
    }
    // Plain Enter — newline (default).
  };

  // Suggestion click
  const onSuggestion = useCallback(
    async (text: string) => {
      if (isStreaming) return;
      setInput('');
      await sendMessage(text);
    },
    [isStreaming, sendMessage],
  );

  const hasMessages = messages.length > 0;
  // Лоадер виден непрерывно, пока стрим идёт и ответный ТЕКСТ ещё не начался.
  // Не завязываемся на «есть ли прямо сейчас бегущий tool» — иначе в паузах
  // между инструментами индикатор мигает (пропадает и появляется).
  const noAnswerTextYet = (streamingMessage?.content.length ?? 0) === 0;
  const showThinking = isStreaming && (awaitingFirstChunk || noAnswerTextYet);

  if (!isOpen) return null;

  const sendDisabled = isStreaming || (!input.trim() && attached.length === 0);

  return (
    <div
      className="ai-panel"
      role="dialog"
      aria-modal="false"
      aria-label="AI-помощник"
      style={{
        position: 'fixed',
        zIndex: zIndex.floatingPanel,
        top: isMobile ? 0 : 16,
        right: isMobile ? 0 : 16,
        bottom: isMobile ? 'auto' : 16,
        height: isMobile ? '100dvh' : 'auto',
        width: isMobile ? '100vw' : 480,
        maxWidth: isMobile ? '100vw' : 'calc(100vw - 32px)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: isMobile ? 0 : 'var(--r-lg)',
        boxShadow: 'var(--e4)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        overscrollBehavior: 'contain',
        animation: 'ai-panel-in 320ms cubic-bezier(0.16,1,0.3,1)',
        paddingTop: isMobile ? 'env(safe-area-inset-top)' : 0,
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
      }}
    >
      {/* Header */}
      <div
        className="row center gap12"
        style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)' }}
      >
        <PulseAvatar size={36} />
        <div className="grow" style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 800, lineHeight: 1.2 }}>AI-помощник</div>
        </div>
        <IconButton
          icon="Plus"
          small
          ghost
          tip="Новая беседа"
          onClick={reset}
          disabled={isStreaming || !hasMessages}
          aria-label="Начать новую беседу"
        />
        <IconButton
          icon="X"
          small
          ghost
          tip="Закрыть (Esc)"
          onClick={closePanel}
          aria-label="Закрыть панель"
        />
      </div>

      {/* Body */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '18px 20px',
          background: 'var(--bg)',
        }}
      >
        {!hasMessages ? (
          <div className="col gap24">
            <div>
              <div className="eyebrow" style={{ color: 'var(--primary-dark)' }}>
                AI-помощник
              </div>
              <div className="h2" style={{ marginTop: 4, marginBottom: 8 }}>
                Чем помочь?
              </div>
              <div className="muted" style={{ fontSize: 13.5, lineHeight: 1.5 }}>
                Спросите про CPL, кампании, аномалии или сверку с CRM — запущу те же скиллы
                Яндекс.Директа, что и в Claude Code.
              </div>
            </div>
            <AiSuggestions onSelect={(t) => void onSuggestion(t)} />
          </div>
        ) : (
          <div className="col">
            {messages.map((m) => {
              // Пока крутится лоадер, пустое стриминговое сообщение не рисуем —
              // его уже представляет AiTyping (со своим аватаром). Иначе на экране
              // оказываются два аватара-плейсхолдера подряд.
              if (showThinking && m.id === streamingMessageId && m.content.length === 0) {
                return null;
              }
              return <AiMessage key={m.id} message={m} />;
            })}
            {showThinking && <AiTyping steps={thinkingSteps} />}
          </div>
        )}
      </div>

      {/* Composer */}
      <div
        style={{
          borderTop: '1px solid var(--border)',
          background: 'var(--surface)',
          padding: 12,
        }}
      >
        <AiUploader
          open={showUploader}
          onClose={() => setShowUploader(false)}
          onFiles={(files) => setAttached((prev) => [...prev, ...files])}
        />

        {attached.length > 0 && (
          <div className="row wrap gap6" style={{ marginBottom: 8 }}>
            {attached.map((f, i) => (
              <span
                key={`${f.name}-${i}`}
                className="chip"
                style={{ background: 'var(--primary-100)', color: 'var(--primary-dark)' }}
              >
                <Icon name="Paperclip" size={13} />
                <span style={{ fontWeight: 600 }}>{f.name}</span>
                <button
                  type="button"
                  className="ai-panel__chip-x"
                  onClick={() => setAttached((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label={`Убрать ${f.name}`}
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="composer">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Спросите о клубе…  (⌘ + Enter — отправить)"
            style={{ resize: 'none', minHeight: 48, maxHeight: MAX_TEXTAREA_HEIGHT, overflowY: 'auto' }}
          />
          <div className="composer__bar">
            <IconButton
              icon="Paperclip"
              small
              ghost
              tip="Прикрепить файл"
              onClick={() => setShowUploader((v) => !v)}
              aria-label="Прикрепить файл"
              style={showUploader ? { color: 'var(--primary)', borderColor: 'var(--primary)' } : undefined}
            />
            <div className="grow" />
            {isStreaming ? (
              <>
                <span
                  className="composer__send-hint muted"
                  style={{ fontSize: 11.5, fontWeight: 600 }}
                >
                  Идёт ответ…
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  icon="Square"
                  onClick={handleStop}
                  aria-label="Остановить ответ"
                >
                  Стоп
                </Button>
              </>
            ) : (
              <>
                <span
                  className="composer__send-hint muted"
                  style={{ fontSize: 11.5, fontWeight: 600 }}
                >
                  ⌘ + Enter
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  icon="Send"
                  onClick={() => void handleSend()}
                  disabled={sendDisabled}
                  aria-label="Отправить сообщение"
                >
                  Отправить
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

async function readFileText(file: File): Promise<string> {
  if (file.size > 5 * 1024 * 1024) {
    return `[Файл ${file.name} > 5MB — содержимое не приложено]`;
  }
  try {
    return await file.text();
  } catch {
    return `[Не удалось прочитать ${file.name}]`;
  }
}
